import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useDeliveryManagement } from "./useHook";
import { MdDelete, MdEdit } from "react-icons/md";
import Loader from "../../Utils/loader";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import OutStationRatesModal from "../../Components/Modals/OutStationRatesModal/OutStationRatesModal";

const OutStationBaseRatesPage = () => {
    const {
        getOutStationBaseRates,
        addOutStationBaseRate,
        updateOutStationBaseRate,
        deleteOutStationBaseRate,
        getAllOutStationZones
    } = useDeliveryManagement();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [outStationData, setOutStationData] = useState([]);
    const [editData, setEditData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [zones, setZones] = useState([]);

    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: 10
    });
    const [offset, setOffset] = useState(0);
    const [limit] = useState(10);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => {
        setIsModalOpen(false);
        setEditData(null);
    };

    const fetchOutStationData = async () => {
        try {
            setIsLoading(true);
            const response = await getOutStationBaseRates(setOutStationData, offset, limit);

            if (response?.pagination) {
                setPagination({
                    totalRecords: response.pagination.totalRecords,
                    totalPages: response.pagination.totalPages,
                    currentPage: response.pagination.currentPage,
                    perPage: response.pagination.perPage
                });
            }
        } catch (error) {
            console.error("Error fetching out-station data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOutStationData();
        getAllOutStationZones(setZones);
    }, [offset]);

    const handlePageChange = (newOffset) => {
        setOffset(newOffset);
    };

    const handleAddOrUpdate = async (formData) => {
        try {
            if (editData) {
                const response = await updateOutStationBaseRate(editData.id, formData);
                if (response) {
                    Swal.fire({
                        icon: "success",
                        title: "Out-Station Updated",
                        text: "Your out-station configuration has been successfully updated!",
                        confirmButtonText: "OK",
                    });
                }
            } else {
                const response = await addOutStationBaseRate(formData);
                if (response) {
                    Swal.fire({
                        icon: "success",
                        title: "Out-Station Added",
                        text: "Your out-station configuration has been successfully added!",
                        confirmButtonText: "OK",
                    });
                }
            }
            await fetchOutStationData();
        } catch (error) {
            console.log(error);
            Swal.fire({
                icon: "error",
                title: "Failed to Save Out-Station",
                text: error?.response?.data?.message,
                confirmButtonText: "OK"
            });
        }
        closeModal();
    };

    const handleEdit = (data) => {
        setEditData(data);
        openModal();
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const response = await deleteOutStationBaseRate(id);
                if (response) {
                    Swal.fire({
                        icon: "success",
                        title: "Deleted!",
                        text: "The out-station configuration has been deleted.",
                        confirmButtonText: "OK",
                    });
                    await fetchOutStationData();
                }
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "Failed to Delete",
                    text: "An error occurred while deleting the out-station configuration. Please try again.",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    if (isLoading) {
        return <Loader message="Loading out-station data..." />;
    }

    return (
        <>
            <div>
                <div className="bg-gray-100 p-4 flex justify-between">
                    <h2 className="text-2xl font-bold">Manage Out-Station Base Rates</h2>
                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                        onClick={openModal}
                    >
                        Add Out-Station Base Rate
                    </button>
                </div>

                <div className="mt-4">
                    <div className="bg-gray-100">
                        <div className="overflow-x-auto mt-6">
                            <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                                        <th className="p-4 text-left">Sr. No</th>
                                        <th className="p-4 text-left">Zone Name</th>
                                        <th className="p-4 text-left">Min Weight</th>
                                        <th className="p-4 text-left">Max Weight</th>
                                        <th className="p-4 text-left">Builty Rate</th>
                                        <th className="p-4 text-left">Coach Rate</th>
                                        <th className="p-4 text-left">Adda Delivery</th>
                                        <th className="p-4 text-left">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {outStationData && outStationData.length > 0 ? (
                                        outStationData.map((item, index) => (
                                            <tr
                                                key={item.id}
                                                className="border-t border-gray-200 hover:bg-gray-50"
                                            >
                                                <td className="p-4">{offset + index + 1}</td>
                                                <td className="p-4">{item.zone?.zone_name || 'N/A'}</td>
                                                <td className="p-4">{item.min_weight}</td>
                                                <td className="p-4">{item.max_weight}</td>
                                                <td className="p-4">{item.builty_rate}</td>
                                                <td className="p-4">{item.coach_rate}</td>
                                                <td className="p-4">{item.adda_delivery}</td>
                                                <td className="px-4 py-2 border-b">
                                                    <div className="flex gap-2">
                                                        <button
                                                            className="bg-primary-dark text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                                                            onClick={() => handleEdit(item)}
                                                        >
                                                            <MdEdit />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.id)}
                                                            className="bg-red-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                                                        >
                                                            <MdDelete />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-6 text-gray-500">
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="mt-6">
                                <GlobalPagination
                                    offset={offset}
                                    limit={limit}
                                    total={pagination.totalRecords}
                                    onPageChange={handlePageChange}
                                    className="mt-4"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <OutStationRatesModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleAddOrUpdate}
                editData={editData}
                zones={zones}
            />
        </>
    );
};

export default OutStationBaseRatesPage;